import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import OperationsShell from "@/components/operations/OperationsShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SHARED_FILTER_KEYS, buildTabSwitchParams }
    from "@/pages/operations/notificationCentreUrl";
import { chipMeta } from "@/pages/operations/notificationCentreChips";
import { loadAllCollapse, writeCollapse }
    from "@/pages/operations/notificationCentreCollapse";
import { safeAttentionRoute, isWorkflowIntelligence }
    from "@/pages/operations/notificationCentreAttention";
import {
    Home, RefreshCw, X, Bell, BellRing, Mail, MessageSquare,
    AlertTriangle, Clock, ChevronRight, ChevronDown, ChevronUp,
    Send, RotateCcw, Info, Shield, Eye, ExternalLink,
    ChevronLeft, ChevronsRight, Archive, ArchiveRestore,
    CheckCircle2, Circle, MailOpen, Filter,
} from "lucide-react";

const PAGE_SIZE = 50;

const STATE_COLORS = {
    queued:      "bg-slate-100 text-slate-700",
    sent:        "bg-blue-100 text-blue-700",
    delivered:   "bg-emerald-100 text-emerald-700",
    failed:      "bg-red-100 text-red-700",
    retrying:    "bg-amber-100 text-amber-700",
    suppressed:  "bg-purple-100 text-purple-700",
    read:        "bg-emerald-100 text-emerald-700",
    accepted:    "bg-emerald-100 text-emerald-700",
    rejected:    "bg-red-100 text-red-700",
    duplicate:   "bg-slate-100 text-slate-700",
};

const CHANNEL_ICONS = { in_app: Bell, email: Mail, sms: MessageSquare, push: BellRing };

// Which URL params are OPERATIONAL filters (reset by "Reset" button).
const EVENT_FILTER_KEYS = [
    "since","until","event_type","source","submission_status",
    "severity","customer_id","project_id","correlation_id","q",
];
const DELIVERY_FILTER_KEYS = [
    "since","until","status","channel","retryable","recipient_role",
    "recipient_id","customer_id","project_id","template_key",
    "appointment_id","correlation_id","error_code","q",
];

function StatePill({ status }) {
    const cls = STATE_COLORS[status] || "bg-slate-100 text-slate-700";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded ${cls}`}
              data-testid={`state-pill-${status}`}>{status || "—"}</span>
    );
}

function ChannelIcon({ channel }) {
    const Icon = CHANNEL_ICONS[channel] || Bell;
    return <Icon size={14} strokeWidth={1.75} className="text-slate-500" aria-hidden />;
}

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

// DEV-016C1 · Attention Centre resolution-route validator lives in
// its own module (`notificationCentreAttention.js`) so a Jest test
// can exercise the sanitisation contract without booting React.

function Label({ htmlFor, children, testid }) {
    return (
        <label htmlFor={htmlFor} data-testid={testid}
               className="flex flex-col text-[11px] font-medium text-slate-600">
            {children}
        </label>
    );
}

function Select({ id, label, value, onChange, options, testid, allLabel = "All" }) {
    return (
        <Label htmlFor={id} testid={`${testid}-label`}>
            <span className="mb-0.5">{label}</span>
            <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value || undefined)}
                    data-testid={testid} aria-label={label}
                    className="rounded border border-slate-300 px-2 py-1 text-sm min-w-[8rem]">
                <option value="">{allLabel}</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </Label>
    );
}

function TextInput({ id, label, value, onChange, testid, placeholder = "" }) {
    return (
        <Label htmlFor={id} testid={`${testid}-label`}>
            <span className="mb-0.5">{label}</span>
            <input id={id} value={value || ""} onChange={(e) => onChange(e.target.value || undefined)}
                   placeholder={placeholder}
                   data-testid={testid} aria-label={label}
                   className="rounded border border-slate-300 px-2 py-1 text-sm min-w-[9rem]" />
        </Label>
    );
}

// ─── Summary cards ─────────────────────────────────────────────────

function SummaryCards({ summary, onCardClick }) {
    if (!summary) return null;
    const { events = {}, deliveries = {}, channels = {} } = summary;
    const items = [
        { key: "events_total",    label: "Events (period)",      value: events.total,     tab: "events" },
        { key: "accepted",        label: "Accepted",             value: events.accepted,  tab: "events", filter: { submission_status: "accepted" } },
        { key: "suppressed_e",    label: "Events suppressed",    value: events.suppressed, tab: "events", filter: { submission_status: "suppressed" } },
        { key: "duplicate",       label: "Duplicate replays",    value: events.duplicate,  tab: "events", filter: { submission_status: "duplicate" } },
        { key: "d_total",         label: "Deliveries",           value: deliveries.total,     tab: "deliveries" },
        { key: "queued",          label: "Queued / pending",     value: deliveries.queued,    tab: "deliveries", filter: { status: "queued" } },
        { key: "sent",            label: "Sent (accepted)",      value: deliveries.sent,      tab: "deliveries", filter: { status: "sent" } },
        { key: "delivered",       label: "Delivered",            value: deliveries.delivered, tab: "deliveries", filter: { status: "delivered" } },
        { key: "failed",          label: "Failed",               value: deliveries.failed,    tab: "deliveries", filter: { status: "failed" } },
        { key: "retryable",       label: "Retryable",            value: deliveries.retryable, tab: "deliveries", filter: { retryable: "true" } },
        { key: "suppressed_d",    label: "Deliveries suppressed", value: deliveries.suppressed, tab: "deliveries", filter: { status: "suppressed" } },
        { key: "in_app",          label: "In-app",               value: channels?.in_app?.total, tab: "deliveries", filter: { channel: "in_app" } },
        { key: "email",           label: "Email",                value: channels?.email?.total, tab: "deliveries", filter: { channel: "email" } },
        { key: "sms",             label: "SMS",                  value: channels?.sms?.total, tab: "deliveries", filter: { channel: "sms" } },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2"
             data-testid="notif-summary-grid" role="list">
            {items.map((it) => (
                <button
                    key={it.key} role="listitem"
                    onClick={() => onCardClick(it.tab, it.filter || {})}
                    data-testid={`notif-summary-${it.key}`}
                    aria-label={`${it.label}: ${it.value ?? 0}. Open filtered view.`}
                    className="text-left rounded-md border border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 px-3 py-2 transition-colors"
                >
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{it.label}</div>
                    <div className="text-lg font-semibold text-slate-900 mt-0.5">{it.value ?? 0}</div>
                </button>
            ))}
            {!summary.push_available && (
                <div className="col-span-2 md:col-span-4 lg:col-span-1 flex items-center justify-center rounded-md border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-500"
                     data-testid="notif-summary-push">Push unavailable</div>
            )}
        </div>
    );
}

// ─── Filter bar ────────────────────────────────────────────────────

function EventFilters({ values, opts, onChange }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3"
             data-testid="notif-filter-bar-events" role="search" aria-label="Events filters">
            <Label htmlFor="ev-since"><span className="mb-0.5">Since</span>
                <input id="ev-since" type="datetime-local" value={values.since || ""}
                       aria-label="Since"
                       onChange={(e) => onChange({ since: e.target.value || undefined })}
                       data-testid="filter-since"
                       className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Label>
            <Label htmlFor="ev-until"><span className="mb-0.5">Until</span>
                <input id="ev-until" type="datetime-local" value={values.until || ""}
                       aria-label="Until"
                       onChange={(e) => onChange({ until: e.target.value || undefined })}
                       data-testid="filter-until"
                       className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Label>
            <Select id="ev-result" label="Result" testid="filter-result"
                    value={values.submission_status}
                    onChange={(v) => onChange({ submission_status: v })}
                    options={opts?.submission_statuses || []} />
            <Select id="ev-type" label="Event type" testid="filter-event-type"
                    value={values.event_type}
                    onChange={(v) => onChange({ event_type: v })}
                    options={opts?.event_types || []} />
            <Select id="ev-source" label="Source/domain" testid="filter-source"
                    value={values.source}
                    onChange={(v) => onChange({ source: v })}
                    options={opts?.sources || []} />
            <Select id="ev-priority" label="Priority" testid="filter-priority"
                    value={values.severity}
                    onChange={(v) => onChange({ severity: v })}
                    options={opts?.severities || []} />
            <Select id="ev-customer" label="Customer" testid="filter-customer"
                    value={values.customer_id}
                    onChange={(v) => onChange({ customer_id: v })}
                    options={opts?.customers || []} />
            <Select id="ev-project" label="Project" testid="filter-project"
                    value={values.project_id}
                    onChange={(v) => onChange({ project_id: v })}
                    options={opts?.projects || []} />
            <TextInput id="ev-corr" label="Correlation ID" testid="filter-correlation-id"
                       value={values.correlation_id}
                       onChange={(v) => onChange({ correlation_id: v })} />
            <TextInput id="ev-q" label="Search" testid="filter-q"
                       value={values.q}
                       onChange={(v) => onChange({ q: v })} placeholder="Text search" />
        </div>
    );
}

function DeliveryFilters({ values, opts, onChange }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3"
             data-testid="notif-filter-bar-deliveries" role="search" aria-label="Deliveries filters">
            <Label htmlFor="d-since"><span className="mb-0.5">Since</span>
                <input id="d-since" type="datetime-local" value={values.since || ""}
                       aria-label="Since"
                       onChange={(e) => onChange({ since: e.target.value || undefined })}
                       data-testid="filter-since"
                       className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Label>
            <Label htmlFor="d-until"><span className="mb-0.5">Until</span>
                <input id="d-until" type="datetime-local" value={values.until || ""}
                       aria-label="Until"
                       onChange={(e) => onChange({ until: e.target.value || undefined })}
                       data-testid="filter-until"
                       className="rounded border border-slate-300 px-2 py-1 text-sm" />
            </Label>
            <Select id="d-state" label="Delivery state" testid="filter-delivery-state"
                    value={values.status}
                    onChange={(v) => onChange({ status: v })}
                    options={opts?.delivery_states || []} />
            <Select id="d-channel" label="Channel" testid="filter-channel"
                    value={values.channel}
                    onChange={(v) => onChange({ channel: v })}
                    options={opts?.channels || []} />
            <Label htmlFor="d-retryable" testid="filter-retryable-label">
                <span className="mb-0.5">Retryable</span>
                <span className="inline-flex items-center gap-1 text-xs">
                    <input id="d-retryable" type="checkbox"
                           checked={values.retryable === "true"}
                           aria-label="Retryable only"
                           onChange={(e) => onChange({ retryable: e.target.checked ? "true" : undefined })}
                           data-testid="filter-retryable" />
                    Retryable only
                </span>
            </Label>
            <Select id="d-role" label="Recipient role" testid="filter-recipient-role"
                    value={values.recipient_role}
                    onChange={(v) => onChange({ recipient_role: v })}
                    options={opts?.recipient_roles || []} />
            <TextInput id="d-recipient" label="Recipient" testid="filter-recipient-id"
                       value={values.recipient_id}
                       onChange={(v) => onChange({ recipient_id: v })} placeholder="ID or email" />
            <Select id="d-customer" label="Customer" testid="filter-customer"
                    value={values.customer_id}
                    onChange={(v) => onChange({ customer_id: v })}
                    options={opts?.customers || []} />
            <Select id="d-project" label="Project" testid="filter-project"
                    value={values.project_id}
                    onChange={(v) => onChange({ project_id: v })}
                    options={opts?.projects || []} />
            <Select id="d-template" label="Event / template" testid="filter-template-key"
                    value={values.template_key}
                    onChange={(v) => onChange({ template_key: v })}
                    options={opts?.event_types || []} />
            <TextInput id="d-appt" label="Appointment / logistics ref" testid="filter-appointment-id"
                       value={values.appointment_id}
                       onChange={(v) => onChange({ appointment_id: v })} />
            <TextInput id="d-corr" label="Correlation ID" testid="filter-correlation-id"
                       value={values.correlation_id}
                       onChange={(v) => onChange({ correlation_id: v })} />
            <Select id="d-reason" label="Failure/suppression reason" testid="filter-reason"
                    value={values.error_code}
                    onChange={(v) => onChange({ error_code: v })}
                    options={opts?.reason_codes || []} />
            <TextInput id="d-q" label="Search" testid="filter-q"
                       value={values.q}
                       onChange={(v) => onChange({ q: v })} placeholder="Text search" />
        </div>
    );
}

// ─── Pagination ────────────────────────────────────────────────────

function Pagination({ page, totalPages, total, onChange, testid = "pagination" }) {
    const tp = totalPages || 1;
    const p = Math.min(Math.max(page, 1), tp);
    return (
        <nav aria-label="Pagination" data-testid={testid}
             className="flex items-center gap-3 text-xs text-slate-600">
            <button onClick={() => onChange(1)} disabled={p <= 1}
                    data-testid={`${testid}-first`}
                    aria-label="First page"
                    className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40">
                « First
            </button>
            <button onClick={() => onChange(p - 1)} disabled={p <= 1}
                    data-testid={`${testid}-prev`}
                    aria-label="Previous page"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 disabled:opacity-40">
                <ChevronLeft size={12}/> Previous
            </button>
            <span data-testid={`${testid}-status`} aria-live="polite">
                Page <strong>{p}</strong> of <strong>{tp}</strong> · <strong>{total ?? 0}</strong> results
            </span>
            <button onClick={() => onChange(p + 1)} disabled={p >= tp}
                    data-testid={`${testid}-next`}
                    aria-label="Next page"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 disabled:opacity-40">
                Next <ChevronRight size={12}/>
            </button>
            <button onClick={() => onChange(tp)} disabled={p >= tp}
                    data-testid={`${testid}-last`}
                    aria-label="Last page"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 disabled:opacity-40">
                Last <ChevronsRight size={12}/>
            </button>
        </nav>
    );
}

// ─── Row helpers (keyboard accessible) ─────────────────────────────

function useRowActivation(onSelect) {
    return useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
        }
    }, [onSelect]);
}

function EventsTable({ data, onOpen, selection, onToggle, onToggleAll, archive }) {
    const rows = data?.events || [];
    const selectedOnPage = rows.filter(r => selection.has(r.event_id)).length;
    const allSelected = rows.length > 0 && selectedOnPage === rows.length;
    const someSelected = selectedOnPage > 0 && selectedOnPage < rows.length;
    const selectAllRef = useRef(null);
    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
    }, [someSelected, allSelected, rows.length]);
    const emptyMsg = archive === "archived"
        ? "No archived events. Actions you archive from Active will appear here."
        : archive === "all"
            ? "No events match the current filters."
            : "No active events. Try switching to Archived or adjusting your filters.";
    return (
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto" data-testid="events-table">
            <table className="min-w-full text-sm">
                <caption className="sr-only">Notification events</caption>
                <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
                    <tr>
                        <th scope="col" className="px-3 py-2">
                            <label className="sr-only" htmlFor="events-select-all">Select all events on page</label>
                            <input id="events-select-all" type="checkbox"
                                   ref={selectAllRef}
                                   checked={allSelected}
                                   onChange={() => onToggleAll(rows.map(r => r.event_id))}
                                   data-testid="events-select-all"
                                   data-indeterminate={someSelected ? "true" : "false"}
                                   aria-checked={someSelected ? "mixed" : allSelected}
                                   aria-label="Select all events on current page" />
                        </th>
                        <th scope="col" className="px-3 py-2 text-left">Received</th>
                        <th scope="col" className="px-3 py-2 text-left">Event</th>
                        <th scope="col" className="px-3 py-2 text-left">Project</th>
                        <th scope="col" className="px-3 py-2 text-left">Recipients</th>
                        <th scope="col" className="px-3 py-2 text-left">Result</th>
                        <th scope="col" className="px-3 py-2 text-right">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500"
                                 data-testid="events-empty"
                                 data-archive-scope={archive}>
                            {emptyMsg}
                        </td></tr>
                    )}
                    {rows.map((r) => (
                        <EventRow key={r.event_id} r={r} onOpen={() => onOpen(r.event_id)}
                                   selected={selection.has(r.event_id)}
                                   onToggle={() => onToggle(r.event_id)} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function EventRow({ r, onOpen, selected, onToggle }) {
    const onKey = useRowActivation(onOpen);
    const unreadCls = r.is_read === false ? "font-medium" : "";
    return (
        <tr tabIndex={0} role="button"
            aria-label={`Event ${r.event_type} · ${r.submission_status}${r.is_read === false ? " · unread" : ""}${r.is_archived ? " · archived" : ""}`}
            onClick={onOpen} onKeyDown={onKey}
            className={`border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 focus:ring-2 focus:ring-emerald-500 cursor-pointer ${r.is_read === false ? "bg-emerald-50/30" : ""}`}
            data-testid={`event-row-${r.event_id}`}
            data-unread={r.is_read === false ? "true" : "false"}
            data-archived={r.is_archived ? "true" : "false"}>
            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                <label className="sr-only" htmlFor={`event-select-${r.event_id}`}>Select event {r.event_type}</label>
                <input id={`event-select-${r.event_id}`} type="checkbox"
                       checked={selected} onChange={onToggle}
                       data-testid={`event-select-${r.event_id}`}
                       aria-label={`Select event ${r.event_type}`} />
            </td>
            <td className={`px-3 py-2 text-slate-600 ${unreadCls}`}>{fmtTime(r.received_at)}</td>
            <td className="px-3 py-2">
                <div className={`text-slate-900 ${unreadCls || ""}`}>{r.event_type}
                    {r.is_read === false && (
                        <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-emerald-600" aria-label="unread" data-testid={`event-unread-dot-${r.event_id}`} />
                    )}
                </div>
                <div className="text-[11px] text-slate-500">{r.severity || "info"}</div>
            </td>
            <td className="px-3 py-2 text-slate-700">
                {r.project ? (
                    <>
                        <div>{r.project.reference}</div>
                        <div className="text-[11px] text-slate-500">{r.project.customer_name}</div>
                    </>
                ) : "—"}
            </td>
            <td className="px-3 py-2 text-slate-700">{r.recipient_count ?? 0}</td>
            <td className="px-3 py-2"><StatePill status={r.submission_status} /></td>
            <td className="px-3 py-2 text-right">
                <button onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        data-testid={`event-view-${r.event_id}`}
                        aria-label={`View details for event ${r.event_id}`}
                        className="text-emerald-700 text-xs hover:underline">
                    View details
                </button>
            </td>
        </tr>
    );
}

function DeliveriesTable({ data, onOpen, selection, onToggle, onToggleAll, archive }) {
    const rows = data?.deliveries || [];
    const selectedOnPage = rows.filter(r => selection.has(r.id)).length;
    const allSelected = rows.length > 0 && selectedOnPage === rows.length;
    const someSelected = selectedOnPage > 0 && selectedOnPage < rows.length;
    const selectAllRef = useRef(null);
    useEffect(() => {
        if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
    }, [someSelected, allSelected, rows.length]);
    const emptyMsg = archive === "archived"
        ? "No archived deliveries. Actions you archive from Active will appear here."
        : archive === "all"
            ? "No deliveries match the current filters."
            : "No active deliveries. Try switching to Archived or adjusting your filters.";
    return (
        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto" data-testid="deliveries-table">
            <table className="min-w-full text-sm">
                <caption className="sr-only">Channel deliveries</caption>
                <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
                    <tr>
                        <th scope="col" className="px-3 py-2">
                            <label className="sr-only" htmlFor="deliveries-select-all">Select all deliveries on page</label>
                            <input id="deliveries-select-all" type="checkbox"
                                   ref={selectAllRef}
                                   checked={allSelected}
                                   onChange={() => onToggleAll(rows.map(r => r.id))}
                                   data-testid="deliveries-select-all"
                                   data-indeterminate={someSelected ? "true" : "false"}
                                   aria-checked={someSelected ? "mixed" : allSelected}
                                   aria-label="Select all deliveries on current page" />
                        </th>
                        <th scope="col" className="px-3 py-2 text-left">Created</th>
                        <th scope="col" className="px-3 py-2 text-left">Channel</th>
                        <th scope="col" className="px-3 py-2 text-left">Recipient</th>
                        <th scope="col" className="px-3 py-2 text-left">Template</th>
                        <th scope="col" className="px-3 py-2 text-left">State</th>
                        <th scope="col" className="px-3 py-2 text-left">Attempts</th>
                        <th scope="col" className="px-3 py-2 text-right">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500"
                                 data-testid="deliveries-empty"
                                 data-archive-scope={archive}>
                            {emptyMsg}
                        </td></tr>
                    )}
                    {rows.map((r) => (
                        <DeliveryRow key={r.id} r={r} onOpen={() => onOpen(r.id)}
                                      selected={selection.has(r.id)}
                                      onToggle={() => onToggle(r.id)} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DeliveryRow({ r, onOpen, selected, onToggle }) {
    const onKey = useRowActivation(onOpen);
    const unreadCls = r.is_read === false ? "font-medium" : "";
    return (
        <tr tabIndex={0} role="button"
            aria-label={`Delivery ${r.channel} · ${r.status}${r.is_read === false ? " · unread" : ""}${r.is_archived ? " · archived" : ""}`}
            onClick={onOpen} onKeyDown={onKey}
            className={`border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 focus:ring-2 focus:ring-emerald-500 cursor-pointer ${r.is_read === false ? "bg-emerald-50/30" : ""}`}
            data-testid={`delivery-row-${r.id}`}
            data-unread={r.is_read === false ? "true" : "false"}
            data-archived={r.is_archived ? "true" : "false"}>
            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                <label className="sr-only" htmlFor={`delivery-select-${r.id}`}>Select delivery {r.channel}</label>
                <input id={`delivery-select-${r.id}`} type="checkbox"
                       checked={selected} onChange={onToggle}
                       data-testid={`delivery-select-${r.id}`}
                       aria-label={`Select delivery ${r.id}`} />
            </td>
            <td className={`px-3 py-2 text-slate-600 ${unreadCls}`}>{fmtTime(r.created_at)}</td>
            <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-slate-700"><ChannelIcon channel={r.channel}/> {r.channel}</span></td>
            <td className="px-3 py-2">
                <div className={`text-slate-800 ${unreadCls || ""}`}>
                    {r.recipient_name || r.recipient_role || "—"}
                    {r.is_read === false && (
                        <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-emerald-600" aria-label="unread" data-testid={`delivery-unread-dot-${r.id}`} />
                    )}
                </div>
                <div className="text-[11px] text-slate-500">{r.recipient_role}</div>
            </td>
            <td className="px-3 py-2 text-slate-700">
                <div>{r.template_key || r.event_key}</div>
                {r.template_version && <div className="text-[11px] text-slate-500">v{r.template_version}</div>}
            </td>
            <td className="px-3 py-2"><StatePill status={r.status} /></td>
            <td className="px-3 py-2 text-slate-700">{r.attempt_number ?? 0}</td>
            <td className="px-3 py-2 text-right">
                <button onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        data-testid={`delivery-view-${r.id}`}
                        aria-label={`View details for delivery ${r.id}`}
                        className="text-emerald-700 text-xs hover:underline">
                    View details
                </button>
            </td>
        </tr>
    );
}

// ─── Detail panels ─────────────────────────────────────────────────

function EventDetail({ eventId, onClose, onOpenDelivery }) {
    const navigate = useNavigate();
    const { data, error } = useSWR(
        eventId ? ["notif-event", eventId] : null,
        () => api.opsNotifEventTimeline(eventId),
    );
    if (!eventId) return null;
    const ev = data?.event;
    const isWorkflow = isWorkflowIntelligence(ev);
    const attentionRoute = isWorkflow ? safeAttentionRoute(
        ev?.template_data?.attention_link || ev?.metadata?.attention_link
    ) : null;
    return (
        <div className="fixed inset-0 z-40" data-testid="event-detail-panel" role="dialog" aria-modal="true" aria-label="Event detail">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[560px] bg-white shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-slate-200">
                    <div className="font-medium">Event detail</div>
                    <button onClick={onClose} data-testid="event-detail-close"
                            aria-label="Close event detail"
                            className="p-1 hover:bg-slate-100 rounded"><X size={16}/></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
                    {error && <div className="text-red-600 text-xs">{formatApiError(error?.response?.data?.detail)}</div>}
                    {!ev ? <div className="text-slate-500">Loading…</div> : (
                        <>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Event type</div>
                                <div className="font-medium text-slate-900" data-testid="event-detail-type">{ev.event_type}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                    {ev.severity} · {ev.source} · {fmtTime(ev.received_at)}
                                </div>
                                {isWorkflow && (
                                    <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] px-2 py-0.5"
                                         data-testid="event-detail-domain-badge">
                                        <Shield size={11}/> Workflow Intelligence
                                    </div>
                                )}
                            </div>
                            {attentionRoute && (
                                <button type="button"
                                        onClick={() => navigate(attentionRoute, { replace: false })}
                                        data-testid="event-detail-attention-link"
                                        aria-label="Open in Attention Centre"
                                        className="w-full inline-flex items-center justify-between gap-2 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 px-3 py-2 text-sm text-indigo-900">
                                    <span className="inline-flex items-center gap-2">
                                        <ExternalLink size={14}/> Open in Attention Centre
                                    </span>
                                    <ChevronRight size={14}/>
                                </button>
                            )}
                            {ev.project && (
                                <div className="rounded border border-slate-200 p-2">
                                    <div className="text-[11px] text-slate-500 mb-0.5">Project</div>
                                    <Link to={`/operations/projects/${ev.project.id}`}
                                          data-testid="event-detail-project-link"
                                          className="text-emerald-700 hover:underline text-sm inline-flex items-center gap-1">
                                        {ev.project.reference} · {ev.project.customer_name}
                                        <ExternalLink size={12}/>
                                    </Link>
                                </div>
                            )}
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Policy result</div>
                                <div className="mt-1"><StatePill status={ev.submission_status}/></div>
                                {ev.policy_decision?.suppressed_reason && (
                                    <div className="text-xs text-slate-600 mt-1">Suppression: {ev.policy_decision.suppressed_reason}</div>
                                )}
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Deliveries</div>
                                <div className="mt-1 space-y-2" data-testid="event-detail-deliveries">
                                    {(data?.deliveries || []).map((d) => (
                                        <button key={d.id}
                                                onClick={() => onOpenDelivery(d.id)}
                                                data-testid={`event-detail-delivery-${d.id}`}
                                                aria-label={`Open delivery ${d.recipient_name || d.recipient_role} · ${d.status}`}
                                                className="w-full text-left rounded border border-slate-200 p-2 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex items-start gap-2">
                                            <ChannelIcon channel={d.channel}/>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-900 text-sm truncate">{d.recipient_name || d.recipient_role}</span>
                                                    <StatePill status={d.status}/>
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {d.template_key} · attempt {d.attempt_number ?? 0}
                                                </div>
                                                {d.explanation && (
                                                    <div className="text-[11px] text-amber-700 mt-1">{d.explanation.title}</div>
                                                )}
                                            </div>
                                            <ChevronRight size={14} className="text-slate-400"/>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <details className="text-xs text-slate-500">
                                <summary className="cursor-pointer">Audit detail</summary>
                                <div className="mt-1 space-y-0.5">
                                    <div>Event ID: <code className="text-[11px]">{ev.event_id}</code></div>
                                    <div>Correlation ID: <code className="text-[11px]">{ev.correlation_id || "—"}</code></div>
                                    <div>Dedup key: <code className="text-[11px] break-all">{ev.dedup_key}</code></div>
                                </div>
                            </details>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function DeliveryDetail({ deliveryId, onClose, onChanged }) {
    const navigate = useNavigate();
    const { data, error, mutate } = useSWR(
        deliveryId ? ["notif-delivery", deliveryId] : null,
        () => api.opsNotifDelivery(deliveryId),
    );
    const [confirmRetry, setConfirmRetry] = useState(false);
    const [confirmResend, setConfirmResend] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const d = data;

    const retry = useCallback(async () => {
        if (!d) return;
        setBusy(true);
        try {
            const key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
            const res = await api.opsNotifRetry(d.id, { reason, idempotencyKey: key });
            const outcome = res?.delivery?.status;
            const err = res?.delivery?.error;
            if (outcome === "suppressed" && err === "outbound_delivery_disabled") {
                toast.warning("Retry suppressed — outbound communication is disabled in this environment.");
            } else if (outcome === "suppressed") {
                toast.warning(`Retry suppressed (${err || "policy"})`);
            } else if (outcome === "failed") {
                toast.error(`Retry failed: ${err || "unknown error"}`);
            } else if (outcome === "delivered") {
                toast.success("Retry delivered");
            } else if (outcome === "sent") {
                toast.success("Retry sent — awaiting provider confirmation");
            } else {
                toast.success(`Retry ${outcome || "processed"}`);
            }
            setConfirmRetry(false); setReason(""); mutate(); onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Retry failed");
        } finally { setBusy(false); }
    }, [d, reason, mutate, onChanged]);

    const resend = useCallback(async () => {
        if (!d) return;
        if ((reason || "").trim().length < 3) {
            toast.error("Please enter a reason (at least 3 characters)");
            return;
        }
        setBusy(true);
        try {
            const key = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
            const res = await api.opsNotifResend(d.id, { reason: reason.trim(), idempotencyKey: key });
            const outcome = res?.new_delivery?.status;
            const err = res?.new_delivery?.error;
            if (outcome === "suppressed" && err === "outbound_delivery_disabled") {
                toast.warning("Resend suppressed — outbound communication is disabled in this environment.");
            } else if (outcome === "suppressed") {
                toast.warning(`Resend suppressed (${err || "policy"})`);
            } else if (outcome === "failed") {
                toast.error(`Resend failed: ${err || "unknown error"}`);
            } else if (outcome === "delivered") {
                toast.success("Resend delivered");
            } else if (outcome === "sent") {
                toast.success("Resend sent — awaiting provider confirmation");
            } else {
                toast.success(`Resend ${outcome || "processed"}`);
            }
            setConfirmResend(false); setReason(""); mutate(); onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Resend failed");
        } finally { setBusy(false); }
    }, [d, reason, mutate, onChanged]);

    if (!deliveryId) return null;

    return (
        <div className="fixed inset-0 z-40" data-testid="delivery-detail-panel" role="dialog" aria-modal="true" aria-label="Delivery detail">
            <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
            <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[560px] bg-white shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-slate-200">
                    <div className="font-medium">Delivery detail</div>
                    <button onClick={onClose} data-testid="delivery-detail-close"
                            aria-label="Close delivery detail"
                            className="p-1 hover:bg-slate-100 rounded"><X size={16}/></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
                    {error && <div className="text-red-600 text-xs">{formatApiError(error?.response?.data?.detail)}</div>}
                    {!d ? <div className="text-slate-500">Loading…</div> : (
                        <>
                            <div className="flex items-center gap-2">
                                <ChannelIcon channel={d.channel}/>
                                <span className="text-slate-900 font-medium">{d.channel}</span>
                                <StatePill status={d.status}/>
                                {isWorkflowIntelligence(d) && (
                                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] px-2 py-0.5"
                                          data-testid="delivery-detail-domain-badge">
                                        <Shield size={11}/> Workflow Intelligence
                                    </span>
                                )}
                            </div>
                            {(() => {
                                if (!isWorkflowIntelligence(d)) return null;
                                const route = safeAttentionRoute(
                                    d?.event?.template_data?.attention_link
                                    || d?.event?.metadata?.attention_link
                                    || d?.payload?.attention_link,
                                );
                                if (!route) return null;
                                return (
                                    <button type="button"
                                            onClick={() => navigate(route, { replace: false })}
                                            data-testid="delivery-detail-attention-link"
                                            aria-label="Open in Attention Centre"
                                            className="w-full inline-flex items-center justify-between gap-2 rounded border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 px-3 py-2 text-sm text-indigo-900">
                                        <span className="inline-flex items-center gap-2">
                                            <ExternalLink size={14}/> Open in Attention Centre
                                        </span>
                                        <ChevronRight size={14}/>
                                    </button>
                                );
                            })()}
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Recipient</div>
                                <div className="text-slate-800" data-testid="delivery-detail-recipient">
                                    {d.recipient_name || d.recipient_role || "—"}
                                </div>
                                <div className="text-[11px] text-slate-500">{d.recipient_role}</div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Template</div>
                                <div className="text-slate-800">{d.template_key || "—"}{d.template_version ? ` · v${d.template_version}` : ""}</div>
                            </div>
                            {d.preview && (
                                <div className="rounded border border-slate-200 p-2 bg-slate-50">
                                    <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                                        <Eye size={11}/> Safe preview
                                        {d.preview.body_redacted && <span className="text-[10px] text-amber-700">(redacted)</span>}
                                    </div>
                                    <div className="mt-1 text-slate-900 font-medium text-sm">{d.preview.subject || "—"}</div>
                                    {d.preview.body_preview && (
                                        <div className="text-slate-700 text-xs whitespace-pre-wrap mt-1">{d.preview.body_preview}</div>
                                    )}
                                    {!d.preview.safe && (
                                        <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                                            <Shield size={11}/> Body hidden — could not be safely redacted.
                                        </div>
                                    )}
                                </div>
                            )}
                            {d.explanation && (
                                <div className="rounded border border-amber-200 bg-amber-50 p-2" data-testid="delivery-explanation">
                                    <div className="flex items-start gap-2">
                                        <Info size={14} className="text-amber-700 mt-0.5"/>
                                        <div>
                                            <div className="text-slate-900 text-sm font-medium">{d.explanation.title}</div>
                                            <div className="text-xs text-slate-700 mt-0.5">{d.explanation.description}</div>
                                            {d.explanation.recommended_action && (
                                                <div className="text-xs text-slate-800 mt-1"><strong>Suggested:</strong> {d.explanation.recommended_action}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Attempts</div>
                                <ol className="mt-1 space-y-1 text-xs" data-testid="delivery-attempts-list">
                                    <li className="flex items-center gap-2">
                                        <Clock size={12}/>
                                        <span>#{d.attempt_number ?? 0} · <StatePill status={d.status}/></span>
                                        <span className="text-slate-500">{fmtTime(d.sent_at || d.created_at)}</span>
                                    </li>
                                    {(d.attempts || []).map((a) => (
                                        <li key={a.id} className="flex items-start gap-2">
                                            <RefreshCw size={12}/>
                                            <div>
                                                <div>{a.kind} by {a.actor?.name || a.actor?.email} · {a.result_status || "—"}</div>
                                                <div className="text-slate-500">{fmtTime(a.created_at)}{a.reason ? ` · "${a.reason}"` : ""}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                            <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                                <button disabled={!d.retryable}
                                        onClick={() => setConfirmRetry(true)}
                                        data-testid="delivery-retry-btn"
                                        aria-label="Retry this delivery"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-amber-600 text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                                    <RotateCcw size={12}/> Retry
                                </button>
                                <button onClick={() => setConfirmResend(true)}
                                        data-testid="delivery-resend-btn"
                                        aria-label="Resend this notification"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-medium">
                                    <Send size={12}/> Resend…
                                </button>
                                {!d.retryable && d.retry_reason && (
                                    <div className="text-[11px] text-slate-500">{d.retry_reason}</div>
                                )}
                            </div>
                            {(confirmRetry || confirmResend) && (
                                <div className="mt-3 rounded border border-slate-300 bg-slate-50 p-3" data-testid="confirm-panel">
                                    <div className="text-sm font-medium text-slate-900 mb-2">
                                        {confirmResend ? "Confirm resend" : "Confirm retry"}
                                    </div>
                                    <label htmlFor="confirm-reason" className="block text-xs text-slate-600 mb-1">
                                        {confirmResend ? "Reason (required, ≥ 3 chars)" : "Reason (optional)"}
                                    </label>
                                    <textarea id="confirm-reason" rows={2} value={reason}
                                              onChange={(e) => setReason(e.target.value)}
                                              data-testid="confirm-reason"
                                              className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                                    <div className="flex items-center gap-2 mt-2">
                                        <button onClick={confirmResend ? resend : retry} disabled={busy}
                                                data-testid="confirm-submit"
                                                className="px-3 py-1 rounded bg-slate-900 text-white text-xs disabled:opacity-40">
                                            {busy ? "Working…" : (confirmResend ? "Confirm resend" : "Confirm retry")}
                                        </button>
                                        <button onClick={() => { setConfirmRetry(false); setConfirmResend(false); setReason(""); }}
                                                data-testid="confirm-cancel"
                                                className="px-3 py-1 rounded border border-slate-300 text-xs">Cancel</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────

export default function OperationsNotificationCentre() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();

    const tab = params.get("tab") || "deliveries";
    const archive = params.get("archive") || "active";
    const currentFilterKeys = tab === "events" ? EVENT_FILTER_KEYS : DELIVERY_FILTER_KEYS;
    const filterValues = useMemo(() => {
        const v = {};
        for (const k of currentFilterKeys) {
            const val = params.get(k);
            if (val) v[k] = val;
        }
        return v;
    }, [params, currentFilterKeys]);
    const activeFilterCount = Object.keys(filterValues).length;
    const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

    // Selection state — cleared when tab / page / material filters change.
    const [selection, setSelection] = useState(() => new Set());
    const selectionKey = `${tab}|${page}|${JSON.stringify(filterValues)}|${archive}`;
    useEffect(() => { setSelection(new Set()); }, [selectionKey]);

    // Collapsible filter panel — per-tab persistence in localStorage.
    // Both tabs are hydrated up-front so switching Events → Deliveries
    // → Events restores each tab's own stored preference.
    const [expandedByTab, setExpandedByTab] = useState(() => loadAllCollapse());
    const filtersExpanded = expandedByTab[tab] ?? true;
    const setFiltersExpanded = useCallback((next) => {
        setExpandedByTab((prev) => {
            const current = prev[tab] ?? true;
            const resolved = typeof next === "function" ? next(current) : next;
            writeCollapse(tab, resolved);
            return { ...prev, [tab]: resolved };
        });
    }, [tab]);

    const selectedEventId = tab === "events" ? params.get("event") : null;
    const selectedDeliveryId = tab === "deliveries" ? params.get("delivery") : null;

    // Internal state changes (filters, pagination, detail) use REPLACE
    // so Back returns to the previous Operations page, not to every
    // intermediate filter state.
    const mergeParams = useCallback((patch, { push = false } = {}) => {
        const p = new URLSearchParams(params);
        for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === "") p.delete(k);
            else p.set(k, String(v));
        }
        setParams(p, { replace: !push });
    }, [params, setParams]);

    // Filter changes → merge and reset to page 1.
    const patchFilters = useCallback((patch) => {
        const p = new URLSearchParams(params);
        for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === "") p.delete(k);
            else p.set(k, String(v));
        }
        p.set("page", "1");
        setParams(p, { replace: true });
    }, [params, setParams]);

    // Summary card click — switch tab, apply matching filter, clear
    // any conflicting filters, reset page. Expands the destination
    // tab's filter panel so the applied filter is immediately visible
    // (spec: summary-card deep-links MAY expand the destination panel).
    const applyCardFilter = useCallback((nextTab, filter) => {
        const p = new URLSearchParams();
        // Preserve since/until (period selection).
        for (const k of ["since", "until"]) {
            const v = params.get(k);
            if (v) p.set(k, v);
        }
        p.set("tab", nextTab);
        for (const [k, v] of Object.entries(filter || {})) {
            p.set(k, String(v));
        }
        p.set("page", "1");
        setParams(p, { replace: true });
        // Expand the destination tab's filter panel so the applied
        // filter is visible — but leave the "other" tab's preference
        // untouched.
        setExpandedByTab((prev) => {
            writeCollapse(nextTab, true);
            return { ...prev, [nextTab]: true };
        });
    }, [params, setParams]);

    const resetFilters = useCallback(() => {
        const p = new URLSearchParams();
        p.set("tab", tab);
        p.set("page", "1");
        setParams(p, { replace: true });
    }, [tab, setParams]);

    // NOT-001C1A · Manual tab switch — preserve only shared filters
    // (since, until, customer_id, project_id, correlation_id, q) and
    // drop every tab-specific parameter, both detail parameters and
    // reset to page 1 via history replacement. Guarantees the URL,
    // the selected tab and the visible filter controls always agree.
    const switchTab = useCallback((nextTab) => {
        if (nextTab === tab && !params.get("event") && !params.get("delivery")) {
            return;
        }
        setParams(buildTabSwitchParams(params, nextTab), { replace: true });
    }, [params, setParams, tab]);

    // Data
    const { data: summary, mutate: mutateSummary } = useSWR(
        ["notif-summary", filterValues.since || "", filterValues.until || ""],
        () => api.opsNotifSummary({ since: filterValues.since, until: filterValues.until }),
    );
    const { data: settings } = useSWR("notif-settings", () => api.opsNotifSettings());
    const { data: opts } = useSWR("notif-filters", () => api.opsNotifFilters());

    const eventsQ = { ...filterValues, archive, page, page_size: PAGE_SIZE };
    const { data: events, mutate: mutateEvents } = useSWR(
        tab === "events" ? ["notif-events", JSON.stringify(eventsQ)] : null,
        () => api.opsNotifEvents(eventsQ),
    );

    const deliveriesQ = {
        ...filterValues,
        retryable: filterValues.retryable === "true" ? true : undefined,
        archive, page, page_size: PAGE_SIZE,
    };
    const { data: deliveries, mutate: mutateDeliveries } = useSWR(
        tab === "deliveries" ? ["notif-deliveries", JSON.stringify(deliveriesQ)] : null,
        () => api.opsNotifDeliveries(deliveriesQ),
    );

    const refresh = () => {
        mutateSummary();
        if (tab === "events") mutateEvents(); else mutateDeliveries();
    };

    const openEvent = (id) => mergeParams({ event: id, delivery: undefined });
    const openDelivery = (id) => mergeParams({ delivery: id, event: undefined });
    const closeEvent = () => mergeParams({ event: undefined });
    const closeDelivery = () => mergeParams({ delivery: undefined });

    // NOT-001C2 · selection + bulk helpers
    const toggleOne = (id) => {
        setSelection((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllVisible = (ids) => {
        setSelection((prev) => {
            const all = ids.every((i) => prev.has(i));
            const next = new Set(prev);
            if (all) ids.forEach((i) => next.delete(i));
            else ids.forEach((i) => next.add(i));
            return next;
        });
    };
    const clearSelection = () => setSelection(new Set());

    const bulkAction = async (kind) => {
        if (selection.size === 0) { toast.error("Nothing selected"); return; }
        const ids = Array.from(selection);
        const subject_type = tab === "events" ? "event" : "delivery";
        try {
            const opFn = {
                read: api.opsNotifTriageMarkRead,
                unread: api.opsNotifTriageMarkUnread,
                archive: api.opsNotifTriageArchive,
                unarchive: api.opsNotifTriageUnarchive,
            }[kind];
            const r = await opFn(subject_type, ids);
            toast.success(
                `${kind === "read" ? "Marked as read" :
                   kind === "unread" ? "Marked as unread" :
                   kind === "archive" ? "Archived" : "Unarchived"} · `
                + `updated ${r.updated ?? 0}, unchanged ${r.unchanged ?? 0}`
                + (r.not_found ? `, not found ${r.not_found}` : "")
            );
            clearSelection();
            refresh();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Bulk action failed");
        }
    };

    const removeChip = (key) => {
        const p = new URLSearchParams(params);
        p.delete(key);
        p.set("page", "1");
        setParams(p, { replace: true });
    };

    const listData = tab === "events" ? events : deliveries;
    const totalPages = listData?.total_pages || 1;
    const total = listData?.total || 0;

    return (
        <OperationsShell>
            <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-4" data-testid="notif-centre-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate("/operations")}
                            data-testid="notif-home-btn"
                            aria-label="Return to Operations home"
                            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <Home size={12}/> Home
                    </button>
                    <h1 className="text-xl font-semibold text-slate-900">Notification Centre</h1>
                    <div className="flex-1" />
                    <button onClick={refresh} data-testid="notif-refresh"
                            aria-label="Refresh Notification Centre"
                            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <RefreshCw size={12}/> Refresh
                    </button>
                </div>

                {settings && settings.outbound_delivery_enabled === false && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2"
                         data-testid="outbound-disabled-banner" role="status">
                        <AlertTriangle size={14}/>
                        Outbound email and SMS delivery is disabled in this environment. Retries and resends will exercise the pipeline but produce suppressed results for out-of-band channels.
                    </div>
                )}

                <SummaryCards summary={summary} onCardClick={applyCardFilter} />

                <div className="flex items-center gap-2" data-testid="notif-tabs" role="tablist" aria-label="Notification centre tabs">
                    <button role="tab" aria-selected={tab === "events"}
                            onClick={() => switchTab("events")}
                            data-testid="tab-events"
                            className={`px-3 py-1 rounded text-xs font-medium ${tab === "events" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                        Events
                    </button>
                    <button role="tab" aria-selected={tab === "deliveries"}
                            onClick={() => switchTab("deliveries")}
                            data-testid="tab-deliveries"
                            className={`px-3 py-1 rounded text-xs font-medium ${tab === "deliveries" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                        Deliveries
                    </button>
                    <div className="flex-1" />
                    {/* Archive scope */}
                    <div className="inline-flex items-center gap-1 text-xs" data-testid="archive-scope">
                        <label htmlFor="archive-select" className="text-slate-600">View</label>
                        <select id="archive-select" value={archive}
                                onChange={(e) => mergeParams({ archive: e.target.value, page: "1" })}
                                data-testid="archive-select"
                                aria-label="Archive scope"
                                className="rounded border border-slate-300 px-2 py-1 text-sm">
                            <option value="active">Active</option>
                            <option value="archived">Archived</option>
                            <option value="all">All</option>
                        </select>
                    </div>
                    <button onClick={resetFilters}
                            data-testid="filter-reset"
                            className="text-xs text-slate-600 inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                        <X size={12}/> Reset filters
                    </button>
                </div>

                {/* Collapsible filter panel */}
                <div className="rounded border border-slate-200 bg-white" data-testid={`filters-panel-${tab}`}>
                    <button
                        type="button"
                        onClick={() => setFiltersExpanded((v) => !v)}
                        aria-expanded={filtersExpanded}
                        aria-controls={`filters-body-${tab}`}
                        data-testid="filters-toggle"
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <Filter size={12}/>
                        <span className="font-medium">Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5"
                                  data-testid="filters-count">
                                {activeFilterCount}
                            </span>
                        )}
                        <div className="flex-1" />
                        {filtersExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                    </button>
                    {!filtersExpanded && activeFilterCount > 0 && (
                        <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="filter-chips">
                            {Object.entries(filterValues).map(([k, v]) => {
                                const { label, valueLabel } = chipMeta(k, v, opts);
                                return (
                                    <span key={k} data-testid={`filter-chip-${k}`}
                                          data-chip-value={String(v)}
                                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 text-[11px] px-2 py-0.5 text-slate-700">
                                        <span className="text-slate-500">{label}:</span>
                                        <span>{valueLabel}</span>
                                        <button onClick={() => removeChip(k)}
                                                data-testid={`filter-chip-remove-${k}`}
                                                aria-label={`Remove ${label} filter`}
                                                className="ml-1 rounded hover:bg-slate-200 p-0.5">
                                            <X size={10}/>
                                        </button>
                                    </span>
                                );
                            })}
                            <button onClick={resetFilters}
                                    data-testid="filters-clear-all"
                                    className="text-[11px] text-slate-600 underline underline-offset-2">
                                Clear all
                            </button>
                        </div>
                    )}
                    {filtersExpanded && (
                        <div id={`filters-body-${tab}`} className="p-1">
                            {tab === "events"
                                ? <EventFilters values={filterValues} opts={opts} onChange={patchFilters} />
                                : <DeliveryFilters values={filterValues} opts={opts} onChange={patchFilters} />}
                        </div>
                    )}
                </div>

                {/* Bulk selection bar */}
                {selection.size > 0 && (
                    <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 text-xs"
                         data-testid="bulk-action-bar" role="region" aria-label="Bulk actions">
                        <span data-testid="bulk-selected-count" className="font-medium text-emerald-800">
                            {selection.size} selected
                        </span>
                        <button onClick={() => bulkAction("read")}
                                data-testid="bulk-mark-read"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                            <MailOpen size={12}/> Mark read
                        </button>
                        <button onClick={() => bulkAction("unread")}
                                data-testid="bulk-mark-unread"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                            <Circle size={12}/> Mark unread
                        </button>
                        {archive !== "archived" ? (
                            <button onClick={() => bulkAction("archive")}
                                    data-testid="bulk-archive"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                                <Archive size={12}/> Archive
                            </button>
                        ) : (
                            <button onClick={() => bulkAction("unarchive")}
                                    data-testid="bulk-unarchive"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                                <ArchiveRestore size={12}/> Unarchive
                            </button>
                        )}
                        <div className="flex-1" />
                        <button onClick={clearSelection} data-testid="bulk-clear"
                                className="text-slate-600 hover:text-slate-900">
                            Clear selection
                        </button>
                    </div>
                )}

                {tab === "events"
                    ? <EventsTable data={events} onOpen={openEvent}
                                    selection={selection}
                                    onToggle={toggleOne}
                                    onToggleAll={toggleAllVisible}
                                    archive={archive} />
                    : <DeliveriesTable data={deliveries} onOpen={openDelivery}
                                        selection={selection}
                                        onToggle={toggleOne}
                                        onToggleAll={toggleAllVisible}
                                        archive={archive} />}

                <div className="flex justify-end">
                    <Pagination page={page} totalPages={totalPages} total={total}
                                onChange={(p) => mergeParams({ page: String(p) })}
                                testid={`pagination-${tab}`} />
                </div>

                <EventDetail eventId={selectedEventId} onClose={closeEvent}
                             onOpenDelivery={(id) => mergeParams({ tab: "deliveries", event: undefined, delivery: id })} />
                <DeliveryDetail deliveryId={selectedDeliveryId} onClose={closeDelivery} onChanged={refresh} />
            </div>
        </OperationsShell>
    );
}
